import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface VideoRevealProps {
    isOpen: boolean;
    onClose: () => void;
    onOpen: () => void;
}

const VideoReveal: React.FC<VideoRevealProps> = ({ isOpen, onClose, onOpen }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [isHovered, setIsHovered] = useState(false);

    // Close on click outside if open
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node) && isOpen) {
                onClose();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen, onClose]);

    return (
        <motion.div
            ref={containerRef}
            className={`relative w-full max-w-4xl mx-auto z-40 mb-4 md:mb-8 ${isOpen ? 'h-auto' : 'h-14 md:h-16'}`}
            initial={false}
            animate={{
                height: isOpen ? 'auto' : 64, // Reduced height for more minimal look
                marginBottom: isOpen ? 48 : 24, // Reduced margin
            }}
            transition={{ type: 'spring', stiffness: 200, damping: 25 }} // Smoother spring
        >
            {/* Background Glow (Ambient) */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[110%] h-[140%] bg-teal-500/15 blur-[50px] rounded-full pointer-events-none opacity-40"></div>

            {/* Main Container */}
            <motion.div
                className={`relative overflow-hidden rounded-[2rem] border border-white/10 backdrop-blur-xl bg-black/40 shadow-2xl
                   ${isOpen ? 'aspect-video' : 'h-full flex items-center justify-center cursor-pointer hover:border-teal-500/40 hover:bg-black/50 group'}`}
                onClick={!isOpen ? onOpen : undefined}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                layout
                whileHover={!isOpen ? { scale: 1.02 } : {}}
                whileTap={!isOpen ? { scale: 0.98 } : {}}
                transition={{ duration: 0.4, ease: [0.23, 1, 0.32, 1] }}
            >
                {/* Closed State Content: "Teaser" Bar */}
                {!isOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="flex items-center gap-4 px-6 md:px-8 w-full justify-center"
                    >
                        {/* Play Button Icon */}
                        <div className="relative w-8 h-8 md:w-10 md:h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-teal-500/10 text-teal-400 border border-teal-500/20 group-hover:scale-110 transition-transform duration-300 shadow-[0_0_15px_rgba(20,184,166,0.1)]">
                            <div className="absolute inset-0 rounded-full bg-teal-400/20 animate-ping"></div>
                            <svg className="w-3.5 h-3.5 md:w-4 md:h-4 ml-0.5" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
                        </div>

                        {/* Text */}
                        <div className="flex flex-col md:flex-row md:items-center gap-1 md:gap-3 text-left">
                            <span className="text-white/90 font-medium text-base md:text-lg tracking-wide group-hover:text-teal-200 transition-colors">
                                Watch How It Works
                            </span>
                            <span className="text-zinc-500 text-xs md:text-sm hidden md:inline-block group-hover:text-zinc-400 transition-colors border-l border-white/10 pl-3">
                                See the future of dentistry
                            </span>
                        </div>

                        {/* Chevron/Expand Icon */}
                        <div className="ml-auto text-zinc-600 group-hover:text-white/80 transition-colors">
                            <svg className={`w-5 h-5 transform transition-transform duration-300 ${isHovered ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5l7 7-7 7" />
                            </svg>
                        </div>
                    </motion.div>
                )}

                {/* Open State Content: YouTube Video */}
                <AnimatePresence>
                    {isOpen && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.5 }}
                            className="absolute inset-0 z-10 bg-black"
                        >
                            {/* Close Button */}
                            <button
                                onClick={(e) => { e.stopPropagation(); onClose(); }}
                                className="absolute top-4 right-4 z-50 p-2 rounded-full bg-black/40 text-white/70 hover:text-white hover:bg-black/60 backdrop-blur-md transition-all duration-300 border border-white/10 hover:rotate-90"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>

                            <iframe
                                width="100%"
                                height="100%"
                                src="https://www.youtube.com/embed/xkFN9_z7CAU?autoplay=1&rel=0&modestbranding=1"
                                title="FuturaSmile Process Video"
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                allowFullScreen
                                className="w-full h-full object-cover"
                            ></iframe>
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.div>
        </motion.div>
    );
};

export default VideoReveal;
